import { useState, useRef } from 'react'
import { useHistory } from 'react-router'
import moment from 'moment'
import featureToggles from 'shared/featureToggles'
import Formsy from 'formsy-react'

import { pendingSelector } from 'api/selectors'
import { setAuthFormState, login } from 'login-and-signup/actions'
import { AUTH_CONTAINER_STATE_FORGOT_PASSWORD } from 'login-and-signup/constants'
import { languageSelector } from '@i18n/selectors'
import { acceptTermsLabelSelector } from 'wordpress/selectors'

import PasswordField from 'shared/components/PasswordField/PasswordField'
import FormsyInput from 'shared/components/Textfield/withFormsy'
import Checkbox from 'shared/components/Checkbox/withFormsy'
import Button from 'shared/components/Button'

const LoginContainer = ({
    setAuthFormState,
    pending,
    translate: t,
    active,
    handleFocus,
    login,
    lang,
    acceptTermsLabel,
}) => {
    const loginTermsAccepted = JSON.parse(localStorage.getItem('loginTermsAccepted') || '{}')
    const [canSubmit, setCanSubmit] = useState(false)
    const [acceptTerms, setAcceptTerms] = useState(false)
    const [showTcCheckbox, setShowTcCheckbox] = useState(false)
    const history = useHistory()
    const formRef = useRef()

    const acceptTermsHandler = event => {
        setAcceptTerms(event.target.checked)
    }

    const handleTcCheckbox = () => {
        const email = formRef?.current?.getModel().email

        if (!loginTermsAccepted[email]) {
            setShowTcCheckbox(true)
        } else if (showTcCheckbox) {
            setShowTcCheckbox(false)
        }
    }

    const enableSubmit = () => {
        setCanSubmit(true)
        handleTcCheckbox()
    }

    const disableSubmit = () => {
        setCanSubmit(false)
        handleTcCheckbox()
    }

    const submit = data => {
        const { email, password, rememberChecked } = data

        if (!loginTermsAccepted[email]) {
            localStorage.setItem(
                'loginTermsAccepted',
                JSON.stringify({ ...loginTermsAccepted, [email]: moment().toISOString() })
            )
        }

        login(email, password, rememberChecked, t)
    }

    const onForgotPasswordClick = () => {
        if (featureToggles.newAuthFlow) {
            let forgotPswUrl = THINX_RESET_PASSWORD_URL_EN
            if (lang === 'fr') {
                forgotPswUrl = THINX_RESET_PASSWORD_URL_FR
            }
            window.location.href = forgotPswUrl
        } else {
            setAuthFormState(AUTH_CONTAINER_STATE_FORGOT_PASSWORD)
        }
    }

    return (
        <section
            tabIndex="0"
            className={`header-login-container ${active ? 'active' : ''}`}
            onFocus={handleFocus}
            aria-labelledby="loginFormTitle"
            role="region">
            <Formsy
                className="header-form login-form"
                onSubmit={submit}
                onValid={enableSubmit}
                onInvalid={disableSubmit}
                ref={formRef}>
                <fieldset>
                    <legend>
                        <h2 id="loginFormTitle">
                            <T>auth.login.title</T>
                        </h2>
                    </legend>
                    <FormsyInput
                        id="email"
                        name="email"
                        label={t('auth.email.label')}
                        required
                        autoFocus={active}
                        aria-describedby="emailDescription"
                        autoComplete="username"
                    />
                    <PasswordField />
                    {showTcCheckbox && (
                        <div className="header-form-strip form-group">
                            <Checkbox
                                required
                                name="acceptTerms"
                                label={acceptTermsLabel}
                                onChange={acceptTermsHandler}
                                aria-describedby="termsDescription"
                            />
                        </div>
                    )}
                    <div className="header-form-strip form-group">
                        <Checkbox name="rememberChecked" className="m-0">
                            <T>auth.remember.me.label</T>
                        </Checkbox>
                        <button
                            type="button"
                            id="loginForgotPasswordBtn"
                            onClick={onForgotPasswordClick}
                            aria-label={t('auth.forgot.password.label')}>
                            {t('auth.forgot.password.label')}
                        </button>
                    </div>
                    <Button
                        className="mt-2"
                        type="submit"
                        isLoading={pending.login === true}
                        onClick={() => history.push({ hash: false })}
                        disabled={!canSubmit || (showTcCheckbox && !acceptTerms)}
                        aria-disabled={!canSubmit || (showTcCheckbox && !acceptTerms)}>
                        <T>auth.login.btn.label</T>
                    </Button>
                </fieldset>
            </Formsy>
        </section>
    )
}

const mapStateToProps = createStructuredSelector({
    pending: pendingSelector,
    lang: languageSelector,
    acceptTermsLabel: acceptTermsLabelSelector,
})

export default compose(
    withTranslate,
    connect(mapStateToProps, {
        login,
        setAuthFormState,
    })
)(LoginContainer)
